import React from 'react'

import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { P, Text } from '@mxenabled/mxui'
import { Icon } from '@mxenabled/mx-icons'

import Donut from '../../../common/components/charts/Donut'

import {
  currencyDisplay,
  InvestmentsAllocationData,
  percentageDisplay,
} from './util/createInvestementsAllocationData'

export interface DonutData {
  id: string
  label?: string
  color: string
  value: number
}
export interface InvestmentsData {
  investmentsData: InvestmentsAllocationData[]
  donutData: DonutData[]
  totalAmount: number
  totalGainLoss: number
  totalGainLossAvailable: boolean
  totalMarketValue: number
  totalGainLossPercentage: number
}
interface InvestementAllocationProps {
  data: InvestmentsData
  hoveredId?: string
  isPercentage?: boolean
  onSelected: (id: string) => void
  onMouseEnter: (id: string) => void
  onMouseLeave: (id: string) => void
  selectedId?: string
  size?: number
  totalLabel: string
}

export const InvestementsAllocationChart: React.FC<InvestementAllocationProps> = ({
  data,
  hoveredId,
  isPercentage,
  onMouseEnter,
  onMouseLeave,
  onSelected,
  selectedId,
  size = 150,
  totalLabel,
}) => {
  const theme = useTheme()
  const isDarkMode = theme.palette.mode === 'dark'

  const selectedInvestments = React.useMemo(
    () =>
      selectedId
        ? data.investmentsData.find(({ guid }: InvestmentsAllocationData) => guid === selectedId)
        : undefined,
    [selectedId],
  )
  const donutData = React.useMemo(() => {
    if (data.donutData.length > 0) return data.donutData

    // Empty Donut zero state
    return [{ id: '0', color: theme.palette.neutral.light, value: 100 }]
  }, [data, theme])

  const gainDisplayColor = isDarkMode ? '#0AC295' : '#09A57F'
  const lossDisplayColor = isDarkMode ? 'grey.300' : 'grey.700'

  const getGainLossDisplay = () => {
    if (selectedInvestments) return percentageDisplay(selectedInvestments.percentage)
    if (!data.totalGainLossAvailable) return '—'
    if (isPercentage) return percentageDisplay(data.totalGainLossPercentage)
    return currencyDisplay(data.totalGainLoss)
  }

  const gainLossDisplayColor = selectedInvestments
    ? 'text.secondary'
    : !data.totalGainLossAvailable
      ? 'text.secondary'
      : data.totalGainLoss >= 0
        ? gainDisplayColor
        : lossDisplayColor

  return (
    <Donut
      data={donutData}
      hoveredId={hoveredId}
      onClick={onSelected}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      selectedId={selectedId}
      size={size}
    >
      <Stack alignItems="center" height="100%" justifyContent="center" width="100%">
        <Stack alignItems="center" justifyContent="center" minHeight={size} minWidth={size}>
          <Stack alignItems="center" sx={{ gap: { xs: 4, xl: 8 }, mb: { xs: 4, xl: 8 } }}>
            <Text color="text.secondary" noWrap={true}>
              {selectedInvestments?.displayName ?? totalLabel}
            </Text>
            <P color="text.primary" variant="h1">
              {currencyDisplay(selectedInvestments?.value ?? data.totalMarketValue)}
            </P>

            <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 4 }}>
              {!selectedInvestments && data.totalGainLossAvailable && (
                <Icon
                  name={data.totalGainLoss >= 0 ? 'trending_up' : 'trending_down'}
                  size={20}
                  sx={{ color: gainLossDisplayColor }}
                />
              )}
              <Text
                sx={{
                  color: gainLossDisplayColor,
                  fontWeight: 700,
                }}
                variant="body2"
              >
                {getGainLossDisplay()}
              </Text>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Donut>
  )
}
